/**
 * Card hover for the homepage's two card families.
 *
 * Why this is GSAP and not CSS: both card stylesheets zoom the image with a
 * `transition: transform 0.7s ease`, and a CSS transition always finishes the
 * tween it is on before starting the next one — so a pointer moving quickly
 * across a grid leaves a wake of cards still finishing an animation the reader
 * has already left behind. gsap.quickTo retargets a running tween in place,
 * which is what makes a fast pointer feel answered instead of queued.
 *
 * Each section's stylesheet drops those CSS transitions under
 * [data-anim='running'] and [data-anim='done'] — the two values that mean GSAP
 * is live and driving the same properties. Under 'off' (GSAP blocked, reduced
 * motion) or with no attribute at all (the merchant turned the section's
 * animation setting off) the CSS hover is left in place, so the degraded page
 * keeps a working hover rather than none.
 *
 * The families differ only in which children move, which is what KINDS holds.
 * The /collections page renders the same card snippet as the category grid but
 * is not listed here, so it keeps its CSS hover.
 */

import { TIMING, motionOnly, bootSection } from '@theme/pag-motion';

const KINDS = [
  {
    key: 'category-hover',
    section: '.pag-category-grid[data-anim]',
    card: '.pag-category-card',
    image: '.pag-category-card-media img, .pag-category-card-media svg',
    lift: '.pag-category-card-overlay h3',
    arrow: '.pag-category-card-arrow',
  },
  {
    key: 'bento-hover',
    section: '.pag-editorial-bento[data-anim]',
    card: '.pag-editorial-tile',
    image: '.pag-editorial-tile-image',
    // The bento's own zoom is 1.04, not the 1.05 the card grids use. The tiles
    // are much larger, so the same ratio would read as a bigger move.
    zoom: 1.04,
    // The tile's caption sits outside its media box and is already doing the
    // work of a label; moving it on hover would fight the composition.
    lift: null,
    arrow: null,
  },
  {
    key: 'product-hover',
    section: '.pag-best-sellers[data-anim]',
    card: '.pag-product-card',
    image: '.pag-product-card-media img, .pag-product-card-media svg',
    // The product card carries its own button, which has its own CSS hover, and
    // the card title is a link in its own right. Only the image moves here.
    lift: null,
    arrow: null,
  },
];

/**
 * A scale setter, as a pair of quickTos.
 *
 * `scale` cannot be quickTo'd. quickTo needs a property that resolves to a single
 * PropTween so it can retarget it in place, and CSSPlugin expands the `scale`
 * shorthand into scaleX and scaleY — so GSAP logs "scale not eligible for reset"
 * and the setter silently does nothing from then on. The failure is invisible
 * until you leave the card: the first hover still lands (the tween initialises
 * against the CSS hover state and writes it inline), and the inline transform
 * then outranks everything, so the image zooms in once and stays there. Both
 * axes are individually eligible, which is what this pairs up.
 */
function quickScale(target, gsap, vars) {
  const setScaleX = gsap.quickTo(target, 'scaleX', vars);
  const setScaleY = gsap.quickTo(target, 'scaleY', vars);

  return (value) => {
    setScaleX(value);
    setScaleY(value);
  };
}

/**
 * Hover, as a set of quickTo setters sharing one card.
 *
 * quickTo binds a setter to one property of one element, which is the cheap way
 * to retarget: no timeline is rebuilt and no tween is allocated per pointer
 * event. Focus is wired alongside hover so a keyboard user gets the feedback the
 * mouse gets.
 */
function bindHover(card, kind, gsap) {
  const image = kind.image ? card.querySelector(kind.image) : null;
  const lift = kind.lift ? card.querySelector(kind.lift) : null;
  const arrow = kind.arrow ? card.querySelector(kind.arrow) : null;

  const { image: imageDur, detail, press: pressDur, ease } = TIMING.hover;

  const setScale = image ? quickScale(image, gsap, { duration: imageDur, ease }) : null;
  const setLiftY = lift ? gsap.quickTo(lift, 'y', { duration: detail, ease }) : null;
  const setArrowX = arrow ? gsap.quickTo(arrow, 'x', { duration: detail, ease }) : null;
  const setArrowY = arrow ? gsap.quickTo(arrow, 'y', { duration: detail, ease }) : null;
  // The press is here rather than in a CSS :active rule because the reveal can
  // leave an inline transform on the card, and an inline transform outranks any
  // stylesheet rule trying to scale the same element.
  const setPress = quickScale(card, gsap, { duration: pressDur, ease });

  const enter = () => {
    setScale?.(kind.zoom ?? 1.05);
    setLiftY?.(-2);
    setArrowX?.(3);
    setArrowY?.(-3);
  };

  const leave = () => {
    setScale?.(1);
    setLiftY?.(0);
    setArrowX?.(0);
    setArrowY?.(0);
    setPress(1);
  };

  const press = () => setPress(0.99);
  const release = () => setPress(1);

  card.addEventListener('pointerenter', enter);
  card.addEventListener('pointerleave', leave);
  card.addEventListener('focus', enter);
  card.addEventListener('blur', leave);
  card.addEventListener('pointerdown', press);
  card.addEventListener('pointerup', release);

  return () => {
    card.removeEventListener('pointerenter', enter);
    card.removeEventListener('pointerleave', leave);
    card.removeEventListener('focus', enter);
    card.removeEventListener('blur', leave);
    card.removeEventListener('pointerdown', press);
    card.removeEventListener('pointerup', release);
    gsap.set([image, lift, arrow, card].filter(Boolean), { clearProps: 'all' });
  };
}

KINDS.forEach((kind) => {
  bootSection(
    kind.section,
    (section) =>
      motionOnly((gsap) => {
        const cards = gsap.utils.toArray(kind.card, section);
        const teardowns = cards.map((card) => bindHover(card, kind, gsap));
        return () => teardowns.forEach((off) => off());
      }),
    kind.key
  );
});
